import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import DataTable from '@common/components/datatable';
import Placeholder from '@common/components/placeholder';
import Spinner from '@common/components/spinner';
import * as integrationFunctionsSelector from '../../selectors/integration-functions';
import {
  importFunction,
  importFunctionsBulk,
  fetchIntegrationFunctions,
} from '../../actions/integration-functions';
import useImportFunctionsColumn from './useImportFunctionsColumn';
import { AlertService } from '@common/services/alert';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string;
};

export const ImportFunctionsModal = ({
  isOpen,
  onClose,
  organisationId,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const unlinkedFunctions = useSelector(
    integrationFunctionsSelector.getUnlinkedFunctions
  );
  const isLoading = useSelector(integrationFunctionsSelector.getIntegrationFunctionsLoading);
  const [isImporting, setIsImporting] = React.useState(false);

  useEffect(() => {
    if (isOpen && organisationId) {
      dispatch(fetchIntegrationFunctions(organisationId));
    }
  }, [isOpen, organisationId, dispatch]);

  const handleImportFunction = async (functionGroup: any) => {
    try {
      await dispatch(
        importFunction(organisationId, {
          name: functionGroup.name,
          externalId: functionGroup.externalId,
        })
      );
      AlertService.success(t('organisation:import_functions_success', {
        name: functionGroup.name,
      }));
    } catch (error) {
      AlertService.error(t('organisation:import_functions_error'));
    }
  };

  const handleImportAll = async () => {
    setIsImporting(true);
    try {
      const functionsData = unlinkedFunctions.map((group) => ({
        name: group.name,
        externalId: Array.isArray(group.externalId)
          ? group.externalId
          : [group.externalId],
      }));
      await dispatch(importFunctionsBulk(organisationId, functionsData));
      AlertService.success(t('organisation:import_functions_success'));
      onClose();
    } catch (error) {
      AlertService.error(t('organisation:import_functions_error'));
    } finally {
      setIsImporting(false);
    }
  };

  const columns = useImportFunctionsColumn({
    onImportFunction: handleImportFunction,
  });

  return (
    <ModalWindow show={isOpen} onHide={onClose} size="small">
      <ModalContent
        title={t('organisation:import_functions_title')}
        onHide={onClose}
        confirmButtonText={t('organisation:import_functions_import_all')}
        onConfirm={handleImportAll}
        confirmButtonLoading={isImporting}
        confirmButtonDisabled={unlinkedFunctions.length === 0 || isLoading}
      >
        {isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
            <Spinner centered />
          </div>
        ) : (
          <DataTable
            columns={columns as any}
            data={unlinkedFunctions}
            emptyView={
              <Placeholder
                title={t('organisation:import_functions_no_unlinked')}
                description={t('organisation:import_functions_no_unlinked')}
              />
            }
          />
        )}
      </ModalContent>
    </ModalWindow>
  );
};
